import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { semanticColors } from 'design-system/tokens/colors';

interface DefaultPlaceholderProps {
  /** Icon to display (e.g., house icon, logo, etc.) */
  icon?: ReactNode;
  /** Message to display (defaults to "Photos Coming Soon") */
  message?: string;
  /** Whether to apply rounded corners (for carousel/card context) */
  round?: boolean;
}

/**
 * Default Placeholder for Gallery with No Images
 * 
 * Vertical layout with icon on top and message text below.
 * Ported from legacy NoImage component with container query support.
 * Fully generic - receives icon and message as props.
 * 
 * @example
 * // In a domain/section component:
 * import NoImageIcon from 'components/icon/noimage-icon';
 * const { t } = useTranslation();
 * 
 * <DefaultPlaceholder
 *   icon={<NoImageIcon />}
 *   message={t(LOCALE_COMMON_PATHS.NO_IMAGE_PHOTOS_COMING_SOON)}
 * />
 */
export function DefaultPlaceholder({ 
  icon,
  message = 'Photos Coming Soon',
  round = false,
}: DefaultPlaceholderProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        backgroundColor: semanticColors.lightGray,
        // Container query support for responsive sizing
        containerType: 'size',
        // Optional rounded corners (for carousel context)
        ...(round && {
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
        }),
      }}
    >
      {/* Icon */}
      {icon && (
        <Box
          sx={{
            width: '136px',
            height: '40%',
            '& svg': {
              width: '100%',
              height: '100%',
            },
          }}
        >
          {icon}
        </Box>
      )}
      
      {/* Text */}
      <Typography
        component="p"
        sx={{
          color: semanticColors.darkNavy,
          // Use container query width units for responsive sizing (6% of container width)
          fontSize: '6cqw',
          fontWeight: 400,
          lineHeight: 1.2,
          textAlign: 'center',
          margin: '0.5em 0',
        }}
        dangerouslySetInnerHTML={
          message.includes('<br') ? { __html: message } : undefined
        }
      >
        {!message.includes('<br') ? message : undefined}
      </Typography>
    </Box>
  );
}
